import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, X, UserPlus, Gamepad2 } from "lucide-react";
import { api } from "../lib/api";
import { onPersonalEvent } from "../hooks/usePersonalSocket";
import Avatar from "./Avatar";

function fmtRemaining(expiresAtIso) {
  if (!expiresAtIso) return "";
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function NavBell() {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ pending_incoming_count: 0, lobby_invites_pending_count: 0 });
  const [incoming, setIncoming] = useState([]);
  const [invites, setInvites] = useState([]);
  const [tick, setTick] = useState(0);  // 1Hz re-render for invite countdowns
  const ref = useRef(null);

  const total = counts.pending_incoming_count + counts.lobby_invites_pending_count;

  const reload = async () => {
    try {
      const [{ data: c }, { data: f }, { data: inv }] = await Promise.all([
        api.get("/friends/counts"),
        api.get("/friends?status=pending_incoming&limit=10"),
        api.get("/lobbies/invites"),
      ]);
      setCounts(c);
      setIncoming(f.items || []);
      setInvites(inv.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { reload(); }, []);
  useEffect(() => onPersonalEvent((evt) => {
    if ([
      "friend_request_received", "friend_request_accepted", "friend_request_declined",
      "lobby_invite_received", "lobby_invite_declined",
    ].includes(evt.type)) reload();
  }), []);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const acceptFriend = async (fid) => { await api.post(`/friends/${fid}/accept`); reload(); };
  const declineFriend = async (fid) => { await api.post(`/friends/${fid}/decline`); reload(); };
  const acceptInvite = async (id) => {
    const { data } = await api.post(`/lobbies/invites/${id}/accept`);
    if (data?.lobby?.lobby_code) window.location.assign(`/lobby/${data.lobby.lobby_code}`);
  };
  const declineInvite = async (id) => { await api.post(`/lobbies/invites/${id}/decline`); reload(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="nav-bell"
              onClick={() => setOpen((v) => !v)}
              className="relative size-9 rounded-lg border border-white/10 bg-zinc-950/70 hover:bg-white/5 grid place-items-center transition">
        <Bell size={15} className="text-zinc-300" />
        {total > 0 && (
          <span data-testid="nav-bell-badge"
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--accent)] text-[10px] font-bold text-black grid place-items-center">
            {total}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="nav-bell-popover"
             className="absolute right-0 mt-2 w-[360px] rounded-xl border border-white/10 bg-zinc-950 shadow-2xl shadow-black/60 z-50 overflow-hidden">
          {/* friend requests */}
          <div className="p-3 border-b border-white/5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <UserPlus size={11}/> Friend requests
            </div>
            {incoming.length === 0 ? (
              <div className="text-xs text-zinc-600 px-1 py-2">No pending requests</div>
            ) : incoming.map((row) => (
              <div key={row.friendship_id}
                   data-testid={`bell-friend-req-${row.friendship_id}`}
                   className="flex items-center justify-between gap-2 px-2 py-2 hover:bg-white/[0.03] rounded-md">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar user={row.user} size="sm" />
                  <div className="text-sm text-zinc-200 truncate">
                    <span className="font-bold">{row.user.username}</span>
                    <span className="ml-1 text-[10px] text-zinc-500 uppercase tracking-widest">{row.user.tier}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => acceptFriend(row.friendship_id)}
                          className="size-7 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 grid place-items-center hover:bg-emerald-500/25">
                    <Check size={13}/>
                  </button>
                  <button onClick={() => declineFriend(row.friendship_id)}
                          className="size-7 rounded-md bg-white/5 border border-white/10 grid place-items-center hover:bg-white/10">
                    <X size={13}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* lobby invites */}
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Gamepad2 size={11}/> Lobby invites
            </div>
            {invites.length === 0 ? (
              <div className="text-xs text-zinc-600 px-1 py-2">No active invites</div>
            ) : invites.map((iv) => {
              const s = iv.lobby_settings_snapshot || {};
              const rem = fmtRemaining(iv.expires_at);
              return (
                <div key={iv.invite_id}
                     data-testid={`bell-invite-${iv.invite_id}`}
                     className="px-2 py-2 hover:bg-white/[0.03] rounded-md">
                  <div className="flex items-center justify-between text-sm gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar user={iv.from_user} size="sm" />
                      <span className="text-zinc-200 truncate">
                        <span className="font-bold">{iv.from_user.username}</span>
                        <span className="text-zinc-500"> · {s.difficulty} {s.game_mode}</span>
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-[var(--accent)]" data-key={tick}>{rem}</span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[10px] text-zinc-500 font-mono">{iv.lobby_code} · {s.current_player_count}/{s.max_players}</span>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => acceptInvite(iv.invite_id)}
                              className="px-2 py-1 rounded-md bg-[var(--accent)] text-black text-[11px] font-bold uppercase tracking-widest">
                        Join
                      </button>
                      <button onClick={() => declineInvite(iv.invite_id)}
                              className="size-6 rounded-md bg-white/5 border border-white/10 grid place-items-center hover:bg-white/10">
                        <X size={11}/>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            <Link to="/friends" className="block mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300">
              Open friends →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
